import React, { ReactNode, useCallback, useState } from "react";
import * as Sentry from "@sentry/react";
import { PageErrorFallback } from "./PageErrorFallback";

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

/**
 * Error boundary component powered by Sentry.
 * Catches JavaScript errors in child components, reports them to Sentry,
 * and displays a fallback UI preventing the entire app from crashing.
 */
function ErrorBoundary({ children, fallback }: ErrorBoundaryProps) {
  const [resetKey, setResetKey] = useState(0);

  const handleReset = useCallback(() => {
    setResetKey((prev) => prev + 1);
  }, []);

  return (
    <Sentry.ErrorBoundary
      key={resetKey}
      fallback={({ error }) => {
        if (fallback) {
          return <>{fallback}</>;
        }
        return (
          <PageErrorFallback
            error={error instanceof Error ? error : null}
            onReset={handleReset}
          />
        );
      }}
      showDialog
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}

export { ErrorBoundary };
export type { ErrorBoundaryProps };
